import { useEffect, useMemo, useReducer, useRef, type Dispatch } from 'react';
import { fmt, useI18n } from '@/i18n';
import { ConfiguratorHeader } from '@/configurator/ConfiguratorHeader';
import { ArtworkUploader } from '@/configurator/artwork/ArtworkUploader';
import { useArtworkWarningTexts } from '@/configurator/artwork/warnings';
import { GiftSetStage, composeLogo, type SetStageApi } from '@/configurator/gift/GiftSetStage';
import {
  GIFT_PRODUCTS,
  GIFT_QTY_PRESETS,
  GIFT_STEPS,
  MAX_ITEMS,
  giftBlocker,
  giftReducer,
  initialGiftState,
  parseNames,
  toGiftConfiguration,
  type GiftAction,
  type GiftItemState,
  type GiftState,
} from '@/configurator/gift/state';
import { INK_SETUPS, containersFor, defaultContainer, getContainer } from '@/configurator/product/model';
import { ProductThumb } from '@/configurator/product/parts';
import { RfqStep } from '@/configurator/rfq/RfqStep';
import '@/styles/configurator.css';

interface StepProps {
  s: GiftState;
  dispatch: Dispatch<GiftAction>;
}

export function GiftConfiguratorPage() {
  const { d } = useI18n();
  const g = d.gift;
  const [s, dispatch] = useReducer(giftReducer, initialGiftState);
  const setRef = useRef<SetStageApi | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const warn = useArtworkWarningTexts();

  useEffect(() => {
    document.title = `${d.cfg.families.gift.title} — RDK`;
  }, [d]);

  const stepId = GIFT_STEPS[s.step];
  const blocker = giftBlocker(s, stepId);
  const names = useMemo(() => parseNames(s.namesText), [s.namesText]);
  const sampleName = s.personal === 'names' && s.showName ? (names[0] ?? g.personal.sample) : null;
  const art = useMemo(() => (s.logo ? composeLogo(s.logo.image, sampleName) : null), [s.logo, sampleName]);
  const labelFor = (i: GiftItemState) => `${d.cfg.families[i.product].name} · ${getContainer(i.containerId).name}`;

  const go = (n: number) => {
    dispatch({ type: 'goto', step: n });
    requestAnimationFrame(() => {
      const top = panelRef.current?.getBoundingClientRect().top ?? 0;
      if (top < 0 || window.innerWidth < 900) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };
  const canReach = (i: number) => {
    if (i <= s.maxStep) return true;
    for (let k = 0; k < i; k++) if (giftBlocker(s, GIFT_STEPS[k])) return false;
    return i === s.maxStep + 1;
  };

  return (
    <main className="cfg" id="main">
      <ConfiguratorHeader
        current="gift"
        title={d.cfg.families.gift.intro}
        steps={GIFT_STEPS.map((id, i) => ({ id, label: g.steps[id], state: i === s.step ? 'current' : i <= s.maxStep ? 'done' : 'todo', enabled: canReach(i) }))}
        onStep={go}
      />
      <div className="cfg__body cfg__body--gift wrap">
        <div className="cfg__stage">
          <div className="stage-frame stage-frame--set">
            <div className="stage-frame__meta mono">
              <span>{g.items.inSet}: {s.items.length}</span>
              <span>{d.gift.pack.options[s.packaging].label}</span>
              <span>
                {s.quantity} {g.sets}
              </span>
            </div>
            {s.items.length ? (
              <GiftSetStage apiRef={setRef} items={s.items} art={art} scale={s.logoScale} offset={s.offset} ink={s.ink} spin={s.spin} labelFor={labelFor} />
            ) : (
              <p className="set-stage__empty">{g.items.empty}</p>
            )}
          </div>
        </div>

        <div className="cfg__panel" ref={panelRef}>
          <p className="cfg__stepof mono">{fmt(d.cfg.stepOf, { n: s.step + 1, total: GIFT_STEPS.length })}</p>
          {stepId === 'items' && <StepItems s={s} dispatch={dispatch} />}
          {stepId === 'logo' && (
            <div className="step">
              <h2 className="step__title">{g.logo.title}</h2>
              <p className="step__lead">{g.logo.lead}</p>
              <ArtworkUploader
                artwork={s.logo}
                status={s.upload.status}
                onArtwork={(logo) => dispatch({ type: 'logo', logo })}
                onUpload={(status, id) => dispatch({ type: 'upload', status, id })}
                designHelp={s.designHelp}
                onDesignHelp={(value) => dispatch({ type: 'designHelp', value })}
              />
            </div>
          )}
          {stepId === 'personal' && <StepPersonal s={s} dispatch={dispatch} count={names.length} />}
          {stepId === 'preview' && <StepPreview s={s} dispatch={dispatch} />}
          {stepId === 'pack' && <StepPack s={s} dispatch={dispatch} />}
          {stepId === 'rfq' && (
            <RfqStep
              quantity={s.quantity}
              skuCount={1}
              targetDate={s.targetDate}
              quantityLabel={g.pack.qtyTitle}
              artwork={s.logo}
              uploadedArtworkId={s.upload.id}
              buildConfiguration={(ids) => toGiftConfiguration(s, s.logo ? warn(s.logo.warnings) : [], ids)}
              exportMockup={async () => (s.logo ? ((await setRef.current?.exportMockup()) ?? null) : null)}
              onArtworkUploaded={(id) => dispatch({ type: 'upload', status: 'done', id })}
              onQuantity={(quantity) => dispatch({ type: 'quantity', quantity })}
              editable={['items', 'packaging', 'artwork', 'personalization', 'logoScale']}
              onEdit={(line) => {
                const map: Record<string, number> = { items: 0, artwork: 1, personalization: 2, logoScale: 3, packaging: 4 };
                if (line in map) go(map[line]);
              }}
              onReset={() => dispatch({ type: 'reset' })}
            />
          )}

          <div className="cfg__nav">
            {blocker && <p className="cfg__blocker">{blocker === 'items' ? g.items.empty : d.cfg.nextRequires.upload}</p>}
            <button type="button" className="btn btn--ghost" disabled={s.step === 0} onClick={() => go(s.step - 1)}>
              {d.common.back}
            </button>
            {stepId !== 'rfq' && (
              <button type="button" className="btn btn--primary" disabled={!!blocker || (stepId === 'pack' && s.quantity < 1)} onClick={() => go(s.step + 1)}>
                {d.common.next}: {g.steps[GIFT_STEPS[s.step + 1]]}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}

function StepItems({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const t = d.gift.items;
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>
      <div className="presets">
        <span className="kicker">{t.presets}</span>
        <div className="seg-pills">
          {t.presetList.map((p) => (
            <button key={p.id} type="button" onClick={() => dispatch({ type: 'preset', id: p.id })} title={p.d}>
              {p.name}
            </button>
          ))}
        </div>
      </div>
      <ul className="gift-items">
        {GIFT_PRODUCTS.map((product) => {
          const it = s.items.find((i) => i.product === product);
          const c = it ? getContainer(it.containerId) : defaultContainer(product);
          const material = it?.material ?? c.defaultMaterial;
          return (
            <li key={product} className={`gift-item${it ? ' is-on' : ''}`}>
              <span className="gift-item__thumb">
                <ProductThumb c={c} material={material} id={`gi-${product}`} />
              </span>
              <div className="gift-item__body">
                <div className="gift-item__head">
                  <span className="choice__name">{d.cfg.families[product].name}</span>
                  <button
                    type="button"
                    className={`btn btn--sm ${it ? 'btn--ghost' : 'btn--dark'}`}
                    disabled={!it && s.items.length >= MAX_ITEMS}
                    aria-pressed={!!it}
                    onClick={() => dispatch({ type: 'toggleItem', product })}
                  >
                    {it ? t.remove : t.add}
                  </button>
                </div>
                {it && (
                  <div className="gift-item__opts">
                    <label className="mini">
                      <span>{t.form}</span>
                      <select value={it.containerId} onChange={(e) => dispatch({ type: 'itemContainer', product, containerId: e.target.value })}>
                        {containersFor(product).map((k) => (
                          <option key={k.id} value={k.id}>
                            {k.name} · {k.volumeMl} мл
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="mini">
                      <span>{t.material}</span>
                      <div className="swatches swatches--sm" role="radiogroup" aria-label={t.material}>
                        {c.materials.map((m) => (
                          <button
                            key={m}
                            type="button"
                            role="radio"
                            aria-checked={m === it.material}
                            aria-label={d.cfg.materials[m].label}
                            title={d.cfg.materials[m].label}
                            className={`swatch swatch--${m}`}
                            onClick={() => dispatch({ type: 'itemMaterial', product, material: m })}
                          />
                        ))}
                      </div>
                    </div>
                    <label className="mini mini--qty">
                      <span>{t.perSet}</span>
                      <input type="number" min={1} max={6} value={it.qty} onChange={(e) => dispatch({ type: 'itemQty', product, qty: Number(e.target.value) || 1 })} />
                    </label>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StepPersonal({ s, dispatch, count }: StepProps & { count: number }) {
  const { d } = useI18n();
  const t = d.gift.personal;
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>
      <div className="choice-grid choice-grid--2" role="radiogroup" aria-label={t.title}>
        {(['none', 'names'] as const).map((m) => (
          <button key={m} type="button" role="radio" aria-checked={s.personal === m} className={`choice${s.personal === m ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'personal', mode: m })}>
            <span className="choice__name">{m === 'none' ? t.none : t.names}</span>
            <span className="choice__desc">{m === 'none' ? t.noneD : t.namesD}</span>
          </button>
        ))}
      </div>
      {s.personal === 'names' && (
        <div className="field">
          <label htmlFor="names">
            {t.listLabel} <span className="mono muted">{fmt(t.count, { n: count })}</span>
          </label>
          <textarea id="names" rows={8} placeholder={t.listPh.replace(/\\n/g, '\n')} value={s.namesText} onChange={(e) => dispatch({ type: 'names', text: e.target.value })} />
          {count > 0 && <p className="notice notice--info">{fmt(t.qtyHint, { n: count })}</p>}
        </div>
      )}
    </div>
  );
}

function StepPreview({ s, dispatch }: StepProps) {
  const { d } = useI18n();
  const t = d.gift.preview;
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <p className="step__lead">{t.lead}</p>
      {!s.logo && <p className="notice notice--info">{d.cfg.editor.noArtwork}</p>}
      <div className="field">
        <label htmlFor="g-scale">
          {t.scale} <span className="mono muted">{Math.round(s.logoScale * 100)}%</span>
        </label>
        <input id="g-scale" type="range" min="20" max="95" value={Math.round(s.logoScale * 100)} onChange={(e) => dispatch({ type: 'scale', value: Number(e.target.value) / 100 })} />
      </div>
      <div className="field">
        <label htmlFor="g-offset">
          {t.offset} <span className="mono muted">{Math.round(s.offset * 100)}%</span>
        </label>
        <input id="g-offset" type="range" min="-30" max="30" value={Math.round(s.offset * 100)} onChange={(e) => dispatch({ type: 'offset', value: Number(e.target.value) / 100 })} />
      </div>
      <label className="check">
        <input type="checkbox" checked={s.spin} onChange={(e) => dispatch({ type: 'spin', value: e.target.checked })} />
        <span>{t.rotate}</span>
      </label>
      {s.personal === 'names' && (
        <label className="check">
          <input type="checkbox" checked={s.showName} onChange={(e) => dispatch({ type: 'showName', value: e.target.checked })} />
          <span>{t.previewName}</span>
        </label>
      )}
    </div>
  );
}

function StepPack({ s, dispatch }: StepProps) {
  const { d, nf } = useI18n();
  const t = d.gift.pack;
  const isPreset = (GIFT_QTY_PRESETS as readonly number[]).includes(s.quantity);
  return (
    <div className="step">
      <h2 className="step__title">{t.title}</h2>
      <h3 className="step__sub">{t.packTitle}</h3>
      <div className="choice-grid choice-grid--3" role="radiogroup" aria-label={t.packTitle}>
        {(['kraft', 'black_box', 'none'] as const).map((p) => (
          <button key={p} type="button" role="radio" aria-checked={s.packaging === p} className={`choice choice--pack${s.packaging === p ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'packaging', value: p })}>
            <span className={`pack-icon pack-icon--${p}`} aria-hidden="true" />
            <span className="choice__name">{t.options[p].label}</span>
            <span className="choice__desc">{t.options[p].d}</span>
          </button>
        ))}
      </div>
      <h3 className="step__sub">{t.qtyTitle}</h3>
      <div className="qty-grid" role="radiogroup" aria-label={t.qtyTitle}>
        {GIFT_QTY_PRESETS.map((q) => (
          <button key={q} type="button" role="radio" aria-checked={s.quantity === q} className={`qty${s.quantity === q ? ' is-on' : ''}`} onClick={() => dispatch({ type: 'quantity', quantity: q })}>
            <span className="qty__n">{nf(q)}</span>
            <span className="qty__u">{d.gift.sets}</span>
          </button>
        ))}
        <label className={`qty qty--custom${!isPreset ? ' is-on' : ''}`}>
          <input
            type="number"
            min={1}
            aria-label={d.cfg.quantity.custom}
            placeholder={d.cfg.quantity.custom}
            value={isPreset ? '' : s.quantity || ''}
            onChange={(e) => dispatch({ type: 'quantity', quantity: Math.max(0, Math.round(Number(e.target.value))) })}
          />
        </label>
      </div>
      <h3 className="step__sub">{t.inkTitle}</h3>
      <div className="seg-pills" role="radiogroup" aria-label={t.inkTitle}>
        {INK_SETUPS.map((ink) => (
          <button key={ink} type="button" role="radio" aria-checked={s.ink === ink} onClick={() => dispatch({ type: 'ink', ink })}>
            {d.cfg.print.inks[ink].label}
          </button>
        ))}
      </div>
      <div className="field">
        <label htmlFor="g-date">
          {d.cfg.quantity.date} <span className="muted">({d.common.optional})</span>
        </label>
        <input id="g-date" type="date" value={s.targetDate} onChange={(e) => dispatch({ type: 'targetDate', date: e.target.value })} />
      </div>
      <p className="muted small">{d.cfg.quantity.lead}</p>
    </div>
  );
}
